/**
 * Getting photos ready in the browser before they go up (Content Studio photo
 * library, GID-Session-025): a sha-256 of the original file (so the same photo
 * isn't stored twice) and a shrink to 2400 px JPEG (a folder of 4 MB phone photos
 * goes up as ~0.5 MB each). The server re-encodes anyway (no metadata).
 */
export const IMAGE_FILE = /\.(jpe?g|png|webp|heic|heif)$/i;

export async function sha256Hex(file) {
  if (!globalThis.crypto?.subtle) return '';
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** A smaller JPEG copy when the browser can read the file; the original otherwise (HEIC, odd formats). */
export async function shrinkImage(file, maxPx = 2400, quality = 0.88) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || typeof createImageBitmap !== 'function') return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) { bmp.close?.(); return file; }
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // a transparent PNG becomes white, not black
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', quality));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}
